import React from 'react';
import styled from 'styled-components';

const DocsPage = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  h1 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 2.5rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  
  h2 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.8rem;
    font-weight: 600;
    color: #ffffff;
    margin: 2rem 0 1rem 0;
    line-height: 1.3;
  }
  
  h3 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.4rem;
    font-weight: 600;
    color: #ffffff;
    margin: 1.5rem 0 0.75rem 0;
    line-height: 1.4;
  }
  
  p {
    font-family: 'Roboto', sans-serif;
    font-size: 1rem;
    line-height: 1.6;
    color: #e5e7eb;
    margin-bottom: 1rem;
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    font-family: 'Roboto', sans-serif;
    font-size: 1rem;
    line-height: 1.6;
    color: #e5e7eb;
    margin-bottom: 0.5rem;
  }
  
  .feature-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
  }
  
  .feature-card {
    background: rgba(168, 85, 247, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
  }
  
  .feature-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #a855f7;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .step {
    background: rgba(168, 85, 247, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
    margin: 1.5rem 0;
  }
  
  .step-number {
    background: #a855f7;
    color: #ffffff;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
  
  .step-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0.75rem;
  }
  
  .step-content {
    font-family: 'Inter', sans-serif;
    color: #e5e7eb;
    line-height: 1.6;
  }
  
  .info-box {
    background: rgba(59, 130, 246, 0.1);
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
  }
  
  .info-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: #3b82f6;
    margin-bottom: 0.5rem;
  }
  
  .info-text {
    font-family: 'Inter', sans-serif;
    color: #d1d5db;
    font-size: 0.95rem;
  }
  
  .warning-box {
    background: rgba(245, 158, 11, 0.1);
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
  }
  
  .warning-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: #f59e0b;
    margin-bottom: 0.5rem;
  }
  
  .warning-text {
    font-family: 'Inter', sans-serif;
    color: #d1d5db;
    font-size: 0.95rem;
  }
  
  .tabs-section {
    background: rgba(16, 185, 129, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
    margin: 2rem 0;
  }
  
  .tab-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #10b981;
    margin-bottom: 1rem;
  }
  
  .tab-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  .tab-list li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
  }
  
  .tab-list li::before {
    content: '📋';
    font-size: 0.875rem;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 2rem;
    }
    
    h2 {
      font-size: 1.5rem;
    }
    
    .feature-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const WalletManagement: React.FC = () => {
  return (
    <DocsPage>
      <h1>Wallet Management</h1>
      
      <p>
        The Mavrk Wallet system provides a comprehensive, Metamask-style interface for managing your token holdings, earnings, and portfolio on Ink L2. Built with advanced features like token locking, cross-chain operations, and real-time USD tracking.
      </p>

      <h2>Overview</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">🏦 Token Holdings Management</h3>
          <ul>
            <li>Add, remove, and track any ERC-20 token</li>
            <li>Real-time balance updates</li>
            <li>USD value tracking</li>
            <li>Portfolio overview dashboard</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">🔒 Token Locker Integration</h3>
          <ul>
            <li>Lock any ERC-20 token for custom durations</li>
            <li>Multiple locks per user per token</li>
            <li>No early withdrawal (locks are final)</li>
            <li>Gas-optimized operations</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">💸 Send & Receive</h3>
          <ul>
            <li>Transfer tokens and ETH to any address</li>
            <li>QR code for easy sharing</li>
            <li>Address validation and verification</li>
            <li>Transaction tracking with explorer links</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">💰 Creator Earnings</h3>
          <ul>
            <li>Track and collect 25% trading fees</li>
            <li>Real-time earnings dashboard</li>
            <li>Automatic fee collection</li>
            <li>Portfolio performance metrics</li>
          </ul>
        </div>
      </div>

      <h2>Accessing the Wallet</h2>

      <div className="step">
        <div className="step-number">1</div>
        <div>
          <h3 className="step-title">Navigate to Profile</h3>
          <div className="step-content">
            <ol>
              <li>Go to the <strong>"Profile"</strong> section</li>
              <li>Scroll down to the <strong>"Wallet"</strong> component</li>
              <li>The wallet interface will load with your holdings</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="tabs-section">
        <div className="tab-title">Wallet Tabs</div>
        <ul className="tab-list">
          <li><strong>Tokens:</strong> Default view showing all your token holdings</li>
          <li><strong>Locks:</strong> View and manage your token locks</li>
          <li><strong>Earnings:</strong> Track your creator earnings</li>
        </ul>
      </div>

      <h2>Token Holdings Management</h2>

      <div className="step">
        <div className="step-number">2</div>
        <div>
          <h3 className="step-title">Adding Tokens</h3>
          <div className="step-content">
            <h4>Method 1: Search and Add</h4>
            <ol>
              <li>Click <strong>"Add Token"</strong> button</li>
              <li><strong>Search by:</strong>
                <ul>
                  <li>Token name (e.g., "Ink Token")</li>
                  <li>Symbol (e.g., "IKA")</li>
                  <li>Contract address</li>
                </ul>
              </li>
              <li><strong>Select the token</strong> from search results</li>
              <li><strong>Confirm addition</strong> to your holdings</li>
            </ol>

            <h4>Method 2: Contract Address</h4>
            <ol>
              <li>Click <strong>"Add Token"</strong> button</li>
              <li><strong>Paste contract address</strong> directly</li>
              <li><strong>Verify token details</strong> (name, symbol, decimals)</li>
              <li><strong>Add to holdings</strong></li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">3</div>
        <div>
          <h3 className="step-title">Managing Holdings</h3>
          <div className="step-content">
            <h4>View Token Details</h4>
            <ul>
              <li><strong>Token Logo:</strong> Visual identification</li>
              <li><strong>Name & Symbol:</strong> Token identification</li>
              <li><strong>Balance:</strong> Current token balance</li>
              <li><strong>USD Value:</strong> Real-time USD equivalent</li>
              <li><strong>Actions:</strong> Buy, Sell, Lock buttons</li>
            </ul>

            <h4>Remove Tokens</h4>
            <ol>
              <li><strong>Find the token</strong> in your holdings</li>
              <li><strong>Click the "X"</strong> or remove button</li>
              <li><strong>Confirm removal</strong> from your portfolio</li>
              <li><strong>Token removed</strong> from holdings (not from wallet)</li>
            </ol>
          </div>
        </div>
      </div>

      <h2>Token Actions</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">💰 Buy Tokens</h3>
          <ol>
            <li>Click <strong>"Buy"</strong> on any token</li>
            <li>Select source token (ETH, WETH, etc.)</li>
            <li>Enter amount to spend</li>
            <li>Review transaction details</li>
            <li>Confirm purchase in wallet</li>
          </ol>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">💸 Sell Tokens</h3>
          <ol>
            <li>Click <strong>"Sell"</strong> on any token</li>
            <li>Enter amount to sell</li>
            <li>Select destination (ETH, WETH, etc.)</li>
            <li>Review transaction details</li>
            <li>Confirm sale in wallet</li>
          </ol>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">🔒 Lock Tokens</h3>
          <ol>
            <li>Click <strong>"Lock"</strong> on any token</li>
            <li>Enter amount to lock</li>
            <li>Set lock duration (days)</li>
            <li>Approve and lock tokens</li>
            <li>Track lock status in Locks tab</li>
          </ol>
        </div>
      </div>

      <h2>Send & Receive</h2>

      <div className="step">
        <div className="step-number">4</div>
        <div>
          <h3 className="step-title">Sending Tokens</h3>
          <div className="step-content">
            <h4>Send ETH</h4>
            <ol>
              <li>Click <strong>"Send"</strong> button</li>
              <li>Enter recipient address</li>
              <li>Enter amount in ETH</li>
              <li>Add memo (optional)</li>
              <li>Confirm transaction</li>
            </ol>

            <h4>Send ERC-20 Tokens</h4>
            <ol>
              <li>Click <strong>"Send"</strong> on specific token</li>
              <li>Enter recipient address</li>
              <li>Enter token amount</li>
              <li>Review gas estimation</li>
              <li>Confirm transaction</li>
            </ol>

            <h4>Send Features</h4>
            <ul>
              <li><strong>Address Validation:</strong> Ensures valid recipient addresses</li>
              <li><strong>Amount Validation:</strong> Prevents sending more than balance</li>
              <li><strong>Gas Estimation:</strong> Accurate gas cost calculation</li>
              <li><strong>Transaction Tracking:</strong> Monitor send progress</li>
              <li><strong>Explorer Links:</strong> Direct links to transaction</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">5</div>
        <div>
          <h3 className="step-title">Receiving Tokens</h3>
          <div className="step-content">
            <h4>Receive Address</h4>
            <ol>
              <li>Click <strong>"Receive"</strong> button</li>
              <li>Copy your address to clipboard</li>
              <li>Share address with sender</li>
              <li>QR code for easy sharing</li>
            </ol>

            <h4>Receive Features</h4>
            <ul>
              <li><strong>Address Display:</strong> Your Ink L2 address</li>
              <li><strong>QR Code:</strong> Easy mobile sharing</li>
              <li><strong>Copy to Clipboard:</strong> One-click copying</li>
              <li><strong>Address Validation:</strong> Verify address format</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Creator Earnings</h2>

      <div className="step">
        <div className="step-number">6</div>
        <div>
          <h3 className="step-title">Earnings Dashboard</h3>
          <div className="step-content">
            <h4>Total Earnings</h4>
            <ul>
              <li><strong>Lifetime Earnings:</strong> Total fees earned from all tokens</li>
              <li><strong>Available to Collect:</strong> Uncollected fees ready for withdrawal</li>
              <li><strong>Earnings History:</strong> Track earnings over time</li>
              <li><strong>Per-Token Breakdown:</strong> Earnings by individual token</li>
            </ul>

            <h4>Collecting Fees</h4>
            <ol>
              <li>View available earnings</li>
              <li>Click <strong>"Collect"</strong> button</li>
              <li>Confirm collection transaction</li>
              <li>Fees transferred to your wallet</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">7</div>
        <div>
          <h3 className="step-title">Earnings Tracking</h3>
          <div className="step-content">
            <h4>Real-Time Updates</h4>
            <ul>
              <li><strong>Live Fee Collection:</strong> Automatic fee collection</li>
              <li><strong>USD Value:</strong> Real-time USD conversion</li>
              <li><strong>Transaction History:</strong> All earnings transactions</li>
              <li><strong>Performance Metrics:</strong> Earnings trends and analysis</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Mobile Experience</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">📱 Mobile Optimization</h3>
          <ul>
            <li>Touch-friendly interface</li>
            <li>Swipe gestures</li>
            <li>Portrait/landscape support</li>
            <li>Fast loading</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">🔗 Mobile Features</h3>
          <ul>
            <li>QR code scanning</li>
            <li>Mobile wallet integration</li>
            <li>Push notifications</li>
            <li>Offline support</li>
          </ul>
        </div>
      </div>

      <h2>Security Features</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">🔒 Wallet Security</h3>
          <ul>
            <li>Private keys never stored on servers</li>
            <li>Local encryption</li>
            <li>Decentralized security model</li>
            <li>User controls private keys</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">🛡️ Platform Security</h3>
          <ul>
            <li>Battle-tested smart contracts</li>
            <li>Reentrancy protection</li>
            <li>Input validation</li>
            <li>Safe token transfers</li>
          </ul>
        </div>
      </div>

      <h2>Best Practices</h2>

      <div className="step">
        <div className="step-number">8</div>
        <div>
          <h3 className="step-title">Portfolio Management</h3>
          <div className="step-content">
            <ol>
              <li><strong>Diversify Holdings:</strong> Don't put all eggs in one basket</li>
              <li><strong>Regular Reviews:</strong> Check portfolio performance regularly</li>
              <li><strong>Risk Management:</strong> Use locking for risk management</li>
              <li><strong>Documentation:</strong> Keep records of important transactions</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">9</div>
        <div>
          <h3 className="step-title">Security Best Practices</h3>
          <div className="step-content">
            <ol>
              <li><strong>Secure Private Keys:</strong> Never share your private keys</li>
              <li><strong>Verify Addresses:</strong> Always verify recipient addresses</li>
              <li><strong>Test Transactions:</strong> Try small amounts first</li>
              <li><strong>Regular Backups:</strong> Backup important wallet data</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">10</div>
        <div>
          <h3 className="step-title">Earnings Optimization</h3>
          <div className="step-content">
            <ol>
              <li><strong>Create Quality Tokens:</strong> Focus on tokens with trading volume</li>
              <li><strong>Monitor Performance:</strong> Track which tokens earn most</li>
              <li><strong>Regular Collection:</strong> Don't let earnings accumulate too long</li>
              <li><strong>Reinvest Strategically:</strong> Use earnings for new projects</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="info-box">
        <div className="info-title">Pro Tip</div>
        <div className="info-text">
          Use the portfolio overview to track your financial progress and make informed decisions about your token holdings and locking strategies. The wallet system is designed to give you complete control over your DeFi assets.
        </div>
      </div>
    </DocsPage>
  );
};

export default WalletManagement;
